import { PayloadAction } from "@reduxjs/toolkit";
import { eventChannel, END } from "redux-saga";
import { takeLatest, all, call, put, take, fork } from "redux-saga/effects";

import { IProgressState } from "infrastructure/helpers/common";
import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { notification } from "infrastructure/notification";
import { createNotificationResult } from "infrastructure/store";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { plexTranslationUploadActions } from "./PlexTranslationUpload.actions";
import { plexTranslationUploadApi as api } from "./PlexTranslationUpload.api";
import { getValidationIssues } from "./PlexTranslationUpload.helpers";

const createUploadChannel = (file: File) => {
  let emit;

  const channel = eventChannel(emitter => {
    emit = emitter;

    return () => {};
  });

  const promise = call(api.uploadPlexTranslation, file, (progress: IProgressState) => {
    emit(progress.value);

    if (progress.completed) {
      emit(END);
    }
  });

  return [promise, channel];
};

function* progressListener(channel) {
  while (true) {
    const data = yield take(channel);

    yield put(plexTranslationUploadActions.uploadPlexTranslationProcessing(data));
  }
}

function* uploadPlexTranslationExecute(action: PayloadAction<File[]>) {
  try {
    const file = action.payload[0];

    if (file != null) {
      const [promise, channel] = createUploadChannel(file);
      yield fork(progressListener, channel);

      yield promise;

      yield put(plexTranslationUploadActions.uploadPlexTranslationCompleted());
    } else {
      yield put(plexTranslationUploadActions.uploadPlexTranslationError(notification.error().useMessageId(displayMessages.error("plexTranslationUploadFileRequired"))));
    }
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.statusCode === HttpStatusCodes.BadRequest) {
        yield put(
          plexTranslationUploadActions.uploadPlexTranslationFailed(
            createNotificationResult({
              validationIssues: getValidationIssues(error.data),
              text: error.message
            }).withNotification(notification.error().useMessageId(displayMessages.error("messagesUpload")))
          )
        );
      }
    } else {
      yield put(plexTranslationUploadActions.uploadPlexTranslationError(notification.error().useMessageId(displayMessages.error("plexTranslationUploadUnexpected"))));
    }
  }
}

function* actionWatcher() {
  yield takeLatest(plexTranslationUploadActions.uploadPlexTranslation.toString(), uploadPlexTranslationExecute);
}

export function* plexTranslationUploadSaga() {
  yield all([actionWatcher()]);
}
